import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import TestimonialCard from "../../../components/common/TestimonialCard";
import { Images } from "../../../utils/Images";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";
import { validateDonationAmount } from "../../../utils/donationValidation";

const TestimonialSection = ({ donationData, donationAmount = 1000 }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);

  useEffect(() => {
    if (!cmsHome) dispatch(fetchCmsHomeAction());
  }, [cmsHome, dispatch]);
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const isAbove1200 = useMediaQuery("(min-width: 1200px)");

  // Dynamic card width and gap based on screen size
  const getCardDimensions = () => {
    if (isMobile) {
      return { cardWidth: 260, gap: 16, cardsPerView: 1 };
    } else if (isTablet) {
      return { cardWidth: 350, gap: 20, cardsPerView: 1.5 };
    } else if (isDesktop) {
      return { cardWidth: 380, gap: 24, cardsPerView: 2 };
    } else {
      return { cardWidth: 414, gap: 24, cardsPerView: 2.5 };
    }
  };

  const { cardWidth, gap, cardsPerView } = getCardDimensions();

  // Map comments to testimonial format
  const testimonialData = donationData?.comments?.map((comment) => ({
    id: comment.id,
    name: comment.username || "Anonymous",
    description: comment.message || "",
    image: comment.profilepic || null,
  })) || [];

  // Calculate maximum possible index
  const maxIndex = Math.max(
    0,
    testimonialData.length - Math.floor(cardsPerView)
  );

  // Update navigation states based on current index
  const updateNavigationStates = () => {
    setCanScrollLeft(currentIndex > 0);
    setCanScrollRight(currentIndex < maxIndex);
  };

  useEffect(() => {
    updateNavigationStates();
  }, [currentIndex, maxIndex]);

  // Reset scroll position when testimonial data changes
  useEffect(() => {
    const commentsLength = donationData?.comments?.length || 0;
    if (commentsLength > 0) {
      setCurrentIndex(0);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          left: 0,
          behavior: "auto",
        });
      }
    }
  }, [donationData?.comments?.length]);

  // Smooth scroll to specific position
  const scrollToPosition = (index) => {
    if (scrollContainerRef.current) {
      const scrollPosition = index * (cardWidth + gap);
      scrollContainerRef.current.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  };

  const handleNext = () => {
    if (currentIndex < maxIndex) {
      const nextIndex = Math.min(currentIndex + 1, maxIndex);
      setCurrentIndex(nextIndex);
      scrollToPosition(nextIndex);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const prevIndex = Math.max(currentIndex - 1, 0);
      setCurrentIndex(prevIndex);
      scrollToPosition(prevIndex);
    }
  };

  // Handle resize to recalculate positions
  useEffect(() => {
    const handleResize = () => {
      // Reset to first card on resize to avoid layout issues
      setCurrentIndex(0);
      scrollToPosition(0);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [cardWidth, gap]);

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : "94.44%",
        borderRadius: isMobile ? "16px" : "36px",
        backgroundColor: "#E8E8E85C",
        // minHeight: isMobile ? 'auto' : '642px',
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        justifyContent: "center",
        mb: isMobile ? "30px" : "40px",
        mt: isMobile ? "30px" : "50px",
        padding: isMobile ? "30px 0px 20px 16px" : "50px 30px 40px 30px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography
            sx={{
              fontSize: isMobile ? "10px" : "13px",
              color: "#131313",
              fontWeight: 400,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              mb: isMobile ? "10px" : "23px",
              display: "flex",
              alignItems: "start",
              justifyContent: "start",
              gap: 1,
            }}
          >
            <Box
              component="span"
              sx={{
                color: "#A9C74E",
                fontSize: "20px",
                lineHeight: 1,
              }}
            >
              ●
            </Box>
            Join and donate us
          </Typography>

          {/* Title */}
          <Typography
            sx={{
              fontFamily: "Red Hat Display",
              fontWeight: 800,
              fontStyle: isMobile ? "ExtraBold" : "normal",
              fontSize: isMobile ? "18px" : isTablet ? "28px" : "36px",
              lineHeight: isMobile ? "28px" : isTablet ? "36px" : "46px",
              letterSpacing: "0%",
              textAlign: "left",
              verticalAlign: "middle",
              textTransform: "capitalize",
              color: "#1D1D1B",
              marginBottom: isMobile ? "20px" : "60px",
              maxWidth: isMobile ? "100%" : isTablet ? "100%" : "946px",
              marginLeft: "0",
            }}
          >
            {(donationData?.totalDonors?.toLocaleString() || "0")} caring souls have already pledged{" "}
            {donationData?.monthly_header_description ?? ""}
          </Typography>
        </Box>
        <Box>
                {/* Navigation Buttons */}
      <Box
        sx={{
          display:isMobile ? "none" : 'flex',
          width: isMobile ? '60px' : '120px',
          gap: isMobile ? '10px' : '20px',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {/* Back Button */}
        <IconButton
          onClick={handlePrevious}
          disabled={!canScrollLeft}
          sx={{
            width: isMobile ? '25px' : '50px',
            height: isMobile ? '25px' : '50px',
            borderRadius: '50px',
            backgroundColor: canScrollLeft ? '#1D1D1B' : '#B9B9B8',
            border: 'none',
            cursor: canScrollLeft ? 'pointer' : 'not-allowed',
            '&:hover': {
              backgroundColor: canScrollLeft ? '#2a2a2a' : '#B9B9B8'
            },
            '&:disabled': {
              backgroundColor: '#B9B9B8'
            }
          }}
        >
          <Box
            component="img"
            src={Images.leftArrow}
            alt="Previous"
            sx={{
              width: isMobile ? '8px' : '15px',
              height: isMobile ? '6px' : '12px',
              filter: canScrollLeft ? 'brightness(0) invert(1)' : 'none'
            }}
          />
        </IconButton>

        {/* Forward Button */}
        <IconButton
          onClick={handleNext}
          disabled={!canScrollRight}
          sx={{
            width: isMobile ? '25px' : '50px',
            height: isMobile ? '25px' : '50px',
            borderRadius: '50px',
            backgroundColor: canScrollRight ? '#1D1D1B' : '#B9B9B8',
            border: 'none',
            cursor: canScrollRight ? 'pointer' : 'not-allowed',
            '&:hover': {
              backgroundColor: canScrollRight ? '#2a2a2a' : '#B9B9B8'
            },
            '&:disabled': {
              backgroundColor: '#B9B9B8'
            }
          }}
        >
          <Box
            component="img"
            src={Images.rightArrow}
            alt="Next"
            sx={{
              width: isMobile ? '8px' : '15px',
              height: isMobile ? '6px' : '12px',
              filter: canScrollRight ? 'brightness(0) invert(1)' : 'none'
            }}
          />
        </IconButton>
      </Box>
        </Box>
      </Box>
      {/* Testimonial Cards Container with Horizontal Scroll */}
      <Box
        sx={{
          width: "100%",
          minHeight: isMobile ? "auto" : "317px",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          ref={scrollContainerRef}
          sx={{
            display: "flex",
            gap: `${gap}px`,
            overflowX: "auto",
            overflowY: "hidden",
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE and Edge
            "&::-webkit-scrollbar": {
              display: "none", // Chrome, Safari, Opera
            },
            padding: "10px 0",
            paddingLeft: "16px", // Add padding for the first card
            paddingRight: "16px", // Add padding for the last card
            scrollSnapType: "x mandatory", // Enable scroll snapping
            "& > *": {
              flexShrink: 0,
              width: `${cardWidth}px`,
              scrollSnapAlign: "start", // Snap to start of each card
            },
          }}
        >
          {testimonialData.length > 0 ? (
            testimonialData.map((testimonial) => (
              <TestimonialCard
                key={testimonial.id}
                testimonial={testimonial}
                cardWidth={cardWidth}
                isMobile={isMobile}
              />
            ))
          ) : (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: isMobile ? "20px" : "40px",
                color: "#7D7D7D",
              }}
            >
              <Typography>No testimonials available yet.</Typography>
            </Box>
          )}
        </Box>
      </Box>
                  {/* Navigation Buttons */}
      <Box
        sx={{
          display:isMobile ? "flex" : 'none ',
          width: isMobile ? '100%' : '120px',
          gap: isMobile ? '10px' : '20px',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {/* Back Button */}
        <IconButton
          onClick={handlePrevious}
          disabled={!canScrollLeft}
          sx={{
            width: isMobile ? '25px' : '50px',
            height: isMobile ? '25px' : '50px',
            borderRadius: '50px',
            backgroundColor: canScrollLeft ? '#1D1D1B' : '#B9B9B8',
            border: 'none',
            cursor: canScrollLeft ? 'pointer' : 'not-allowed',
            '&:hover': {
              backgroundColor: canScrollLeft ? '#2a2a2a' : '#B9B9B8'
            },
            '&:disabled': {
              backgroundColor: '#B9B9B8'
            }
          }}
        >
          <Box
            component="img"
            src={Images.leftArrow}
            alt="Previous"
            sx={{
              width: isMobile ? '8px' : '15px',
              height: isMobile ? '6px' : '12px',
              filter: canScrollLeft ? 'brightness(0) invert(1)' : 'none'
            }}
          />
        </IconButton>

        {/* Forward Button */}
        <IconButton
          onClick={handleNext}
          disabled={!canScrollRight}
          sx={{
            width: isMobile ? '25px' : '50px',
            height: isMobile ? '25px' : '50px',
            borderRadius: '50px',
            backgroundColor: canScrollRight ? '#1D1D1B' : '#B9B9B8',
            border: 'none',
            cursor: canScrollRight ? 'pointer' : 'not-allowed',
            '&:hover': {
              backgroundColor: canScrollRight ? '#2a2a2a' : '#B9B9B8'
            },
            '&:disabled': {
              backgroundColor: '#B9B9B8'
            }
          }}
        >
          <Box
            component="img"
            src={Images.rightArrow}
            alt="Next"
            sx={{
              width: isMobile ? '8px' : '15px',
              height: isMobile ? '6px' : '12px',
              filter: canScrollRight ? 'brightness(0) invert(1)' : 'none'
            }}
          />
        </IconButton>
      </Box>
      {/* Centered Donate Button */}
      <Box
        sx={{
          width: '100%',
          display: isMobile ? 'none' : 'flex', // Hide on mobile screens
          justifyContent: 'center',
          alignItems: 'center',
          mt: isMobile ? '20px' : '30px',
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            const amount = donationAmount ?? 1000;
            const validation = validateDonationAmount(amount, cmsHome, false, "₹");
            if (!validation.isValid) {
              alert(validation.error);
              return;
            }
            const params = new URLSearchParams();
            if (donationData?.id) {
              params.append('id', donationData.id);
            }
            if (donationData?.title) {
              params.append('title', donationData.title);
            }
            params.append('amount', String(amount));
            navigate(`/donate-monthly-page?${params.toString()}`);
          }}
          sx={{
            width: '196px',
            height: '50px',
            borderRadius: '10px',
            paddingTop: '18px',
            paddingRight: '28px',
            paddingBottom: '18px',
            paddingLeft: '28px',
            background: '#F04951',
            boxShadow: '0px 4px 0px 0px #141414',
            textTransform: 'none',
            '&:hover': { background: '#F04951' },
          }}
        >
          <Box
            sx={{
              fontWeight: 700,
              fontStyle: 'Bold',
              fontSize: '14px',
              lineHeight: '14px',
              letterSpacing: '0%',
              textAlign: 'center',
              verticalAlign: 'middle',
              color: '#FFFFFF',
            }}
          >
            Donate Monthly Now
          </Box>
        </Button>
      </Box>
    </Box>
  );
};

export default TestimonialSection;
